import * as React from 'react'
import { Navigate, Route } from 'react-router-dom'
import type { PathRouteProps } from 'react-router-dom'
import config from '../config'
import { PermissionDeniedNotice } from './PermissionDeniedNotice'
import type { RouteComponentProps } from './types'
import { V5RouteElement } from './v5RouteElement'

type LegacyRestrictedProps = Omit<PathRouteProps, 'element'> & {
  isPermissionGranted?: boolean
  /**
   * v5: ignored in v6 matching (kept for call-site compatibility during migration).
   * Tracked for removal / hook conversions: https://app.asana.com/1/1172821616024733/project/1214950637969298/task/1212721266950206
   */
  exact?: boolean
  /** Force remount when the URL pattern is stable but params change (v5 `key` on Route). */
  key?: React.Key
  component?: React.ComponentType<any>
  render?: (props: RouteComponentProps) => React.ReactNode
  element?: React.ReactNode
  children?: React.ReactNode
}

function buildRestrictedElement(props: LegacyRestrictedProps): React.ReactNode {
  const { isPermissionGranted, component: Component, render, element, children } = props

  if (!isPermissionGranted) {
    return <PermissionDeniedNotice />
  }

  if (!config.user.onboarded) {
    return <Navigate to="/onboarding/shopgoodwill" replace />
  }

  if (render) {
    return <V5RouteElement render={render} />
  }

  if (Component) {
    return <V5RouteElement component={Component} />
  }

  return (
    <>
      {element}
      {children}
    </>
  )
}

/**
 * Returns a real `<Route />` element so it can be used as a direct child of `<Routes>`.
 * React Router v6 does not allow arbitrary wrapper components as `Routes` children—only `Route` /
 * `React.Fragment`—so we build the real `Route` here and keep class/render targets on `V5RouteElement`
 * inside the route `element`. New screens should use hooks (`useNavigate`, `useParams`, …) and
 * standard `<Route element={…}>` instead of this helper when practical.
 */
export function restrictedRoute(props: LegacyRestrictedProps) {
  const {
    isPermissionGranted: _i,
    component: _c,
    render: _r,
    element: _e,
    children: _ch,
    exact: _ex,
    ...routeProps
  } = props
  return React.createElement(Route, {
    ...routeProps,
    element: buildRestrictedElement(props)
  } as any)
}
